/**
 * AppThemeProvider — Full theme control for Apps runtime.
 * Supports mode, colors, typography, borderRadius, headerStyle.
 */
import React from 'react';
import { createTheme, ThemeProvider, useTheme } from '@mui/material/styles';

/** Named theme presets — pick one, no manual color tweaking needed. */
const THEMES = {
  ocean: { name: 'Ocean', primary: '#0d47a1', secondary: '#0277bd', accent: '#4fc3f7' },
  default: { name: 'Classic', primary: '#1976d2', secondary: '#9c27b0', accent: '#42a5f5' },
  sunset: { name: 'Sunset', primary: '#e65100', secondary: '#ff6f00', accent: '#ffab40' },
  forest: { name: 'Forest', primary: '#1b5e20', secondary: '#388e3c', accent: '#81c784' },
  midnight: { name: 'Midnight', primary: '#1a237e', secondary: '#3949ab', accent: '#7986cb' },
  coral: { name: 'Coral', primary: '#c2185b', secondary: '#e91e63', accent: '#f48fb1' },
  lavender: { name: 'Lavender', primary: '#6a1b9a', secondary: '#8e24aa', accent: '#ce93d8' },
  slate: { name: 'Slate', primary: '#37474f', secondary: '#546e7a', accent: '#90a4ae' },
  teal: { name: 'Teal', primary: '#00695c', secondary: '#00897b', accent: '#4db6ac' },
  amber: { name: 'Amber', primary: '#f57c00', secondary: '#ffa726', accent: '#ffd54f' },
};

// Legacy keys for backward compatibility
const PRESETS = Object.fromEntries(
  Object.entries(THEMES).map(([k, v]) => [k, { primary: v.primary, secondary: v.secondary, accent: v.accent }])
);
PRESETS.default = { primary: '#1976d2', secondary: '#9c27b0', accent: '#42a5f5' };

function getColorsFromConfig(t) {
  const themeId = t?.themeId || t?.colorPreset || 'default';
  const preset = THEMES[themeId] || PRESETS[themeId] || PRESETS.default;
  const colors = preset?.primary ? preset : PRESETS.default;
  return {
    primary: t?.primaryColor || colors.primary,
    secondary: t?.secondaryColor || colors.secondary,
    accent: t?.accentColor || colors.accent,
  };
}

export default function AppThemeProvider({ config, darkMode, children }) {
  const parentTheme = useTheme();
  const t = config?.theme || {};
  const colors = getColorsFromConfig(t);

  // Resolve mode: 'auto' uses parent (platform) theme
  const resolvedMode = t.mode === 'dark' ? 'dark' : t.mode === 'light' ? 'light' : (darkMode ? 'dark' : 'light');
  const radius = typeof t.borderRadius === 'number' ? t.borderRadius : 8;

  const theme = createTheme({
    breakpoints: parentTheme.breakpoints,
    palette: {
      mode: resolvedMode,
      primary: {
        main: colors.primary,
        light: colors.accent,
        dark: colors.primary,
      },
      secondary: {
        main: colors.secondary || colors.primary,
      },
      background: {
        default: resolvedMode === 'dark' ? '#121212' : '#f8fafc',
        paper: resolvedMode === 'dark' ? '#1e1e1e' : '#ffffff',
      },
    },
    typography: {
      ...parentTheme.typography,
      fontFamily: t.fontFamily || parentTheme.typography?.fontFamily || '"Roboto", "Helvetica", "Arial", sans-serif',
      h6: {
        ...parentTheme.typography?.h6,
        fontWeight: 600,
      },
    },
    shape: {
      borderRadius: radius,
    },
    // Keeps a Studio preview's simulated device width in effect for the app's media queries.
    ...(parentTheme.components?.MuiUseMediaQuery
      ? { components: { MuiUseMediaQuery: parentTheme.components.MuiUseMediaQuery } }
      : {}),
  });

  return (
    <ThemeProvider theme={theme}>
      {children}
    </ThemeProvider>
  );
}

export { THEMES, PRESETS, getColorsFromConfig };
